import React, { useState } from "react";
import ExcelJS from "exceljs";
import { useSelector } from "react-redux";
import {
  getMappings,
  getOutputColumnsData,
  getInputColumnsData,
  getFileSettings,
  getOutputFileSettings,
} from "../store/workspaceSlice";
import { readRulesAndExecuteOnFile } from "../functions/runRules";
import {
  downloadWorksheetAsCSV,
  prepareWorkSheetFromCSVFilePapaParser,
  readFileAsText,
  extractExcelRows,
  applyOutputFileSettings,
} from "../utils";
import ViewInputFile from "./ViewInputFile";
import { smallTickIcon, uploadIcon } from "../assets/icons";

export const ReadFile = () => {
  const currentMaps = useSelector(getMappings);
  const fileSettings = useSelector(getFileSettings);
  const outputFileSettings = useSelector(getOutputFileSettings);
  const [parsedCSV, setParsedCSV] = useState([]);
  const outputColsData = useSelector(getOutputColumnsData);
  const inputColsData = useSelector(getInputColumnsData);
  const inputFile = useSelector((state) => state.workspace.inputFile);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleFileUpload = async () => {
    const file = selectedFile;
    if (!file) return;

    const fileName = file?.name || "output.csv";
    const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
    const isExcel =
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.name.endsWith(".xlsx");

    if (!isCSV && !isExcel) {
      console.error(
        "Unsupported file type. Please upload a CSV or Excel file.",
      );
      return;
    }

    try {
      // Create workbook and worksheets
      const wb = new ExcelJS.Workbook();
      let worksheet, outputSheet;

      if (isCSV) {
        // Handle CSV file
        const csvData = await readFileAsText(file);
        worksheet = wb.addWorksheet("inputsheet");
        worksheet = prepareWorkSheetFromCSVFilePapaParser(
          csvData,
          worksheet,
          setParsedCSV,
        );
      } else {
        // Handle Excel file
        await wb.xlsx.load(await file.arrayBuffer());
        worksheet = wb.getWorksheet(1);

        // Extract and process Excel data
        const extractedRows = extractExcelRows(worksheet);
        setParsedCSV(extractedRows);
      }

      // Common processing for both file types
      outputSheet = wb.addWorksheet("OutputSheet");
      let { outputSheet: generatedSheet, summary } = readRulesAndExecuteOnFile(
        worksheet,
        outputSheet,
        currentMaps,
        inputColsData,
        outputColsData,
        fileSettings,
        outputFileSettings,
        fileName,
      );

      // Update outputSheet reference
      // applyOutputFileSettings
      applyOutputFileSettings(generatedSheet, outputFileSettings, fileName);
    } catch (error) {
      console.error("Error processing file:", error);
    }
  };

  return (
    <div className="w-full">
      {/* File Upload Section */}
      <div className="mb-8">
        <div className="w-full border-2 border-dashed border-blue-400 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-150 transition-all duration-300 shadow-sm">
          <label
            htmlFor="file-upload"
            className="flex px-6 py-8 flex-col items-center justify-center space-y-3 text-center text-blue-700 cursor-pointer w-full h-full"
          >
            <div className="w-16 h-16 bg-blue-200 rounded-full flex items-center justify-center mb-2">
              {uploadIcon}
            </div>
            <span className="text-lg font-semibold">
              Choose a file to execute mappings
            </span>
            <span className="text-sm text-gray-600">
              CSV or Excel files only (Max 10MB)
            </span>
            {selectedFile && (
              <div className="mt-4 px-4 py-2 bg-green-100 border border-green-300 rounded-lg">
                <span className="text-sm text-green-700 font-medium flex items-center">
                  {smallTickIcon}
                  Selected: {selectedFile.name}
                </span>
              </div>
            )}
            <input
              id="file-upload"
              type="file"
              accept=".csv, .xlsx, .xls"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              className="hidden"
            />
          </label>
        </div>
      </div>
      <div className="flex justify-end mb-4">
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg shadow transition-all duration-200 disabled:opacity-50"
          onClick={handleFileUpload}
          disabled={!selectedFile}
        >
          Execute Rule on File
        </button>
      </div>
      {/* Modal for viewing file */}
      <ViewInputFile
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        fileData={inputFile}
      />
    </div>
  );
};
